import { Blocks, Compass, Cpu, LayoutTemplate, Search, Wrench } from 'lucide-react';
import { type ElementType, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';

import { useHandbook } from './HandbookContext';
import { pagePath, searchHandbook } from './handbookContent';
import type { HandbookGroupId, HandbookPageDef } from './handbookTypes';

export const GROUP_ICONS: Record<HandbookGroupId, ElementType> = {
  start: Compass,
  architecture: Cpu,
  integrations: Blocks,
  templates: LayoutTemplate,
  operations: Wrench,
};

/** `shortcut` binds the `/` key; give it to exactly one search box on screen. */
export function HandbookSearch({ autoFocus, large, shortcut }: { autoFocus?: boolean; large?: boolean; shortcut?: boolean }) {
  const navigate = useNavigate();
  const handbook = useHandbook();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const hits = useMemo(() => searchHandbook(handbook, query).slice(0, 8), [handbook, query]);

  useEffect(() => {
    if (!shortcut) return undefined;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (event.key === '/' && target?.tagName !== 'INPUT' && target?.tagName !== 'TEXTAREA') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shortcut]);

  const go = (page: HandbookPageDef) => {
    setQuery('');
    setOpen(false);
    navigate(pagePath(page));
  };

  return (
    <div className="relative">
      <Search className={cn('pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground', large ? 'h-4 w-4' : 'h-3.5 w-3.5')} />
      <input
        ref={inputRef}
        autoFocus={autoFocus}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive((index) => Math.min(index + 1, hits.length - 1));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((index) => Math.max(index - 1, 0));
          } else if (event.key === 'Enter' && hits[active]) {
            go(hits[active].page);
          } else if (event.key === 'Escape') {
            setOpen(false);
            inputRef.current?.blur();
          }
        }}
        placeholder="Search the handbook"
        aria-label="Search the handbook"
        className={cn(
          'w-full rounded-lg border border-input bg-background pl-9 pr-10 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          large ? 'h-12 text-base' : 'h-9',
        )}
      />
      {shortcut ? <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border border-border px-1.5 font-mono text-[10px] text-muted-foreground">/</kbd> : null}
      {open && query.trim() ? (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-border bg-popover shadow-2xl">
          {hits.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">Nothing matches “{query}”.</div>
          ) : (
            <ul role="listbox">
              {hits.map((hit, index) => {
                const Icon = GROUP_ICONS[hit.page.group];
                return (
                  <li key={pagePath(hit.page)} role="option" aria-selected={index === active}>
                    <button
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => go(hit.page)}
                      className={cn('flex w-full gap-3 px-4 py-3 text-left', index === active ? 'bg-secondary' : '')}
                    >
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground">{hit.page.title}</span>
                        <span className="block truncate text-xs text-muted-foreground">{hit.snippet ?? hit.page.summary}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
